import { serializeValue, inferFieldType } from "../utils/firestoreValueUtils";

export const WINS_ATTENDANCE_COLLECTION = "Wins Attendance";

/**
 * Operators available to the field-filter builder. `needsValue` toggles the
 * value input, `list` splits the value on commas, `textOnly` restricts to
 * string-like fields. Every operator is applied client-side against loaded rows
 * so no composite Firestore index is ever required.
 */
export const FILTER_OPERATORS = [
  { id: "==", label: "== (equals)", needsValue: true },
  { id: "!=", label: "!= (not equals)", needsValue: true },
  { id: ">", label: "> (greater than)", needsValue: true },
  { id: "<", label: "< (less than)", needsValue: true },
  { id: ">=", label: ">= (greater or equal)", needsValue: true },
  { id: "<=", label: "<= (less or equal)", needsValue: true },
  { id: "contains", label: "contains", needsValue: true, textOnly: true },
  { id: "startsWith", label: "starts with", needsValue: true, textOnly: true },
  { id: "endsWith", label: "ends with", needsValue: true, textOnly: true },
  { id: "in", label: "in (comma list)", needsValue: true, list: true },
  { id: "not-in", label: "not-in (comma list)", needsValue: true, list: true },
  { id: "array-contains", label: "array-contains", needsValue: true },
  { id: "array-contains-any", label: "array-contains-any (list)", needsValue: true, list: true },
  { id: "is-null", label: "is null", needsValue: false },
  { id: "is-not-null", label: "is not null", needsValue: false },
];

const OPERATOR_MAP = new Map(FILTER_OPERATORS.map((o) => [o.id, o]));

export function getOperator(id) {
  return OPERATOR_MAP.get(id) || FILTER_OPERATORS[0];
}

/** Detect a Firestore DocumentReference-like object. */
function isReference(value) {
  return Boolean(
    value &&
      typeof value === "object" &&
      typeof value.path === "string" &&
      typeof value.id === "string" &&
      value.parent
  );
}

/** Richer type detection than inferFieldType (adds reference). */
export function detectFieldType(value) {
  if (isReference(value)) return "reference";
  return inferFieldType(value);
}

/**
 * Determine the dominant type per field across the loaded rows so the toolbar
 * can render the correct smart input (date picker, switch, multi-select, ...).
 */
export function collectFieldTypes(rows) {
  const counts = new Map();
  rows.forEach((r) => {
    const data = r?.data || {};
    Object.keys(data).forEach((key) => {
      const type = detectFieldType(data[key]);
      if (type === "null") return;
      const bucket = counts.get(key) || {};
      bucket[type] = (bucket[type] || 0) + 1;
      counts.set(key, bucket);
    });
  });
  const result = {};
  counts.forEach((bucket, key) => {
    result[key] = Object.entries(bucket).sort((a, b) => b[1] - a[1])[0]?.[0] || "string";
  });
  return result;
}

/** All distinct field names present in the loaded rows (excluding id). */
export function collectFieldNames(rows) {
  const set = new Set();
  rows.forEach((r) => Object.keys(r?.data || {}).forEach((k) => set.add(k)));
  return [...set].sort();
}

/** Distinct primitive values for a field (used to populate multi-select inputs). */
export function collectDistinctValues(rows, field, max = 200) {
  const set = new Set();
  for (const r of rows) {
    const v = r?.data?.[field];
    if (v == null) continue;
    if (Array.isArray(v)) {
      v.forEach((item) => set.add(String(item)));
    } else if (typeof v !== "object") {
      set.add(String(v));
    }
    if (set.size >= max) break;
  }
  return [...set].sort();
}

/** Convert any Firestore value into a millisecond timestamp, or null. */
export function toMillis(value) {
  if (value == null) return null;
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate().getTime();
    } catch {
      return null;
    }
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") {
    // Heuristic: treat 10-digit numbers as seconds.
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

function toComparableString(value) {
  if (value == null) return "";
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate().toISOString();
    } catch {
      return String(value);
    }
  }
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") return JSON.stringify(serializeValue(value));
  return String(value);
}

function toNumber(value) {
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate().getTime();
    } catch {
      return NaN;
    }
  }
  const n = Number(value);
  return Number.isNaN(n) ? NaN : n;
}

/**
 * Evaluate a single filter against a raw cell value.
 * @param {*} cellValue raw Firestore value
 * @param {string} op operator id
 * @param {string} target user-provided comparison value
 * @param {string} fieldType detected field type
 */
export function evaluateFilter(cellValue, op, target, fieldType) {
  const operator = getOperator(op);

  if (op === "is-null") return cellValue == null || cellValue === "";
  if (op === "is-not-null") return !(cellValue == null || cellValue === "");

  if (operator.needsValue && (target == null || target === "")) return true;

  const numericField = fieldType === "number" || fieldType === "timestamp";

  switch (op) {
    case "==":
    case "!=": {
      let equal;
      if (fieldType === "boolean") {
        equal = String(cellValue) === String(target).toLowerCase();
      } else if (numericField) {
        equal = toNumber(cellValue) === toNumber(target);
      } else {
        equal = toComparableString(cellValue).toLowerCase() === String(target).toLowerCase();
      }
      return op === "==" ? equal : !equal;
    }
    case ">":
    case "<":
    case ">=":
    case "<=": {
      if (numericField) {
        const a = fieldType === "timestamp" ? toMillis(cellValue) : toNumber(cellValue);
        const b = fieldType === "timestamp" ? toMillis(target) : toNumber(target);
        if (a == null || b == null || Number.isNaN(a) || Number.isNaN(b)) return false;
        if (op === ">") return a > b;
        if (op === "<") return a < b;
        if (op === ">=") return a >= b;
        return a <= b;
      }
      const cmp = toComparableString(cellValue).localeCompare(String(target));
      if (op === ">") return cmp > 0;
      if (op === "<") return cmp < 0;
      if (op === ">=") return cmp >= 0;
      return cmp <= 0;
    }
    case "contains":
      return toComparableString(cellValue).toLowerCase().includes(String(target).toLowerCase());
    case "startsWith":
      return toComparableString(cellValue).toLowerCase().startsWith(String(target).toLowerCase());
    case "endsWith":
      return toComparableString(cellValue).toLowerCase().endsWith(String(target).toLowerCase());
    case "in":
    case "not-in": {
      const list = String(target)
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      const hit = list.includes(toComparableString(cellValue).toLowerCase());
      return op === "in" ? hit : !hit;
    }
    case "array-contains": {
      const arr = Array.isArray(cellValue) ? cellValue.map((v) => String(v).toLowerCase()) : [];
      return arr.includes(String(target).toLowerCase());
    }
    case "array-contains-any": {
      const arr = Array.isArray(cellValue) ? cellValue.map((v) => String(v).toLowerCase()) : [];
      const list = String(target)
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      return list.some((item) => arr.includes(item));
    }
    default:
      return true;
  }
}

/**
 * Apply the list of field filters (AND) to a set of rows.
 * Each filter: { id, field, op, value, type }.
 */
export function applyFieldFilters(rows, filters) {
  const active = (filters || []).filter((f) => f.field && f.op);
  if (!active.length) return rows;
  return rows.filter((row) =>
    active.every((f) => evaluateFilter(row?.data?.[f.field], f.op, f.value, f.type))
  );
}

/** Build a searchable haystack string for a row (id + all serialized values). */
export function rowHaystack(row) {
  const parts = [row?.id || ""];
  const data = row?.data || {};
  Object.keys(data).forEach((key) => {
    parts.push(key);
    parts.push(toComparableString(data[key]));
  });
  return parts.join(" ").toLowerCase();
}

/** Debounced-friendly global search across every loaded field. */
export function applyGlobalSearch(rows, term) {
  const q = String(term || "").trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => rowHaystack(row).includes(q));
}

/**
 * Split text into { text, match } segments for highlighting.
 * Returns a single non-match segment when there is no term.
 */
export function splitHighlight(text, term) {
  const str = String(text ?? "");
  const q = String(term || "").trim();
  if (!q) return [{ text: str, match: false }];
  const lower = str.toLowerCase();
  const needle = q.toLowerCase();
  const segments = [];
  let index = 0;
  let found = lower.indexOf(needle, index);
  while (found !== -1) {
    if (found > index) segments.push({ text: str.slice(index, found), match: false });
    segments.push({ text: str.slice(found, found + needle.length), match: true });
    index = found + needle.length;
    found = lower.indexOf(needle, index);
  }
  if (index < str.length) segments.push({ text: str.slice(index), match: false });
  return segments.length ? segments : [{ text: str, match: false }];
}
